import type {
  LookalikePersona,
  LookalikeView,
  WeightedValue,
} from '@repo/contracts';

/**
 * Three colours plus a muted "Other" — not a product choice, a constraint
 * `apps/web/design.md`'s reference data-viz palette already documents:
 * `viz-1`/`viz-2`/`viz-3` are the only categorical slots validated for a
 * scatter's all-pairs colour comparison, and a fourth distinct hue would put
 * two colours on screen together that fail contrast/colourblind-safety
 * checks. Both the field view (bucketed by company size) and the similarity
 * view (bucketed by cluster, capped at three by `@repo/persona-lookalikes`)
 * share this same four-way scheme so switching views doesn't also reshuffle
 * what a colour means.
 */
const SLOT_CLASSES = ['fill-viz-1', 'fill-viz-2', 'fill-viz-3'] as const;
const OTHER_CLASS = 'fill-viz-muted';

const SLOT_LEGEND_CHIPS = ['bg-viz-1', 'bg-viz-2', 'bg-viz-3'] as const;
const OTHER_LEGEND_CHIP = 'bg-viz-muted';

export function slotFillClass(slot: number | null): string {
  return slot === null ? OTHER_CLASS : (SLOT_CLASSES[slot] ?? OTHER_CLASS);
}

export function slotChipClass(slot: number | null): string {
  return slot === null
    ? OTHER_LEGEND_CHIP
    : (SLOT_LEGEND_CHIPS[slot] ?? OTHER_LEGEND_CHIP);
}

/** The company sizes worth their own colour: the top three by weight in this
 *  spec's own schema — never a fixed real-world size band, since the model
 *  writes whatever bands fit the seed persona's segment. Everything else
 *  folds into "Other" (see this file's own doc on why three). */
export function topCompanySizes(view: LookalikeView): string[] {
  return [...view.spec.schema.companySizes]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 3)
    .map((entry: WeightedValue) => entry.value);
}

/** Which colour slot a lookalike's company size falls into for the field
 *  view — `null` means "Other". */
export function companySizeSlot(
  lookalike: LookalikePersona,
  top: readonly string[],
): number | null {
  const index = top.indexOf(lookalike.companySize);
  return index >= 0 ? index : null;
}
